import { ApiError } from "@/api";

/**
 * Files per multipart upload request: the backend's request bound (at most ten). It bounds one request, not a
 * selection. A deployment configured lower answers `too_many_files` with its `max`, and the rest re-splits.
 */
export const MULTIPART_REQUEST_FILES = 10;
/** Waits on a saturated server (429) per request before reporting that request failed. */
const MAX_BUSY_WAITS = 5;
const MAX_WAIT_MS = 60_000;

/** One request's files by their index in the selection (`indices[i]` is `files[i]`), and what it came to. */
export type RequestOutcome<T> =
  | { start: number; indices: number[]; files: File[]; uploaded: T[] }
  | { start: number; indices: number[]; files: File[]; error: unknown };

/** Wait before retrying a saturated request: Retry-After or exponential, plus up to 25% jitter. */
export function busyDelayMs(retryAfter: number | null, tries: number, random: () => number = Math.random): number {
  const floor = Math.max((retryAfter ?? 0) * 1000, Math.min(MAX_WAIT_MS, 1000 * 2 ** tries));
  return Math.round(floor + Math.min(5_000, floor * 0.25) * random());
}

function tooManyMax(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const detail = (error.body as { detail?: { error_code?: string; max?: unknown } } | undefined)?.detail;
  const max = detail?.error_code === "too_many_files" ? detail.max : null;
  return typeof max === "number" && max > 0 ? max : null;
}

/** Waits `ms`, or until `signal` aborts (then resolves early: the caller checks the signal). */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    }
    signal?.addEventListener("abort", done, { once: true });
  });
}

type Opts = {
  split: boolean;
  wait?: (ms: number) => Promise<void>;
  random?: () => number;
  signal?: AbortSignal;
  /** Whether the file at this index is still wanted (the person may remove it while earlier requests run). */
  keep?: (index: number) => boolean;
};

/**
 * Upload `files` in order over bounded requests (one at a time), reporting each request's outcome as it
 * finishes so successes appear at once and a failed request costs only its own files. A saturated server (429)
 * is waited out with its Retry-After. With `split` off, everything goes in one request as before. Once `signal`
 * aborts (the composer unmounted, or the files were removed) nothing more is sent or reported, and a file `keep`
 * no longer wants is left out of every later request and retry.
 */
export async function uploadInRequests<T>(
  files: File[],
  upload: (files: File[], signal?: AbortSignal) => Promise<T[]>,
  onRequest: (outcome: RequestOutcome<T>) => void,
  opts: Opts,
): Promise<void> {
  let size = opts.split ? MULTIPART_REQUEST_FILES : Math.max(1, files.length);
  const kept = (index: number) => opts.keep?.(index) ?? true;
  let next = 0;
  while (next < files.length && !opts.signal?.aborted) {
    const indices: number[] = [];
    let end = next;
    for (; end < files.length && indices.length < size; end++) if (kept(end)) indices.push(end);
    if (indices.length === 0) break; // everything left was removed
    const max = await uploadChunk(indices, files, upload, onRequest, { ...opts, keep: kept }); // NOSONAR: Each response sets the next chunk limit; one request at a time bounds upload memory.
    if (max !== null) size = max;
    else next = end;
  }
}

async function uploadChunk<T>(
  requested: number[],
  files: File[],
  upload: (files: File[], signal?: AbortSignal) => Promise<T[]>,
  onRequest: (outcome: RequestOutcome<T>) => void,
  opts: Opts & { keep: (index: number) => boolean },
): Promise<number | null> {
  const { signal } = opts;
  const wait = opts.wait ?? ((ms: number) => sleep(ms, signal));
  for (let busy = 0; !signal?.aborted; ) {
    const indices = requested.filter(opts.keep); // a file removed meanwhile (e.g. during a busy wait) stays out
    if (indices.length === 0) return null;
    const chunk = indices.map((i) => files[i]);
    try {
      const uploaded = await upload(chunk, signal);
      if (!signal?.aborted) onRequest({ start: indices[0], indices, files: chunk, uploaded });
      return null;
    } catch (error) {
      const next = afterFailure(error, indices, chunk, busy, onRequest, opts);
      if (next !== "wait") return next;
      await wait(busyDelayMs((error as ApiError).retryAfterSeconds, busy++, opts.random)); // NOSONAR: a busy server's backoff must end before the retry.
    }
  }
  return null;
}

/**
 * Where a failed request goes next: "wait" to wait out a saturated server (429) and retry, the server's smaller
 * request size to re-split at, or null once settled (reported failed, or dropped because `signal` aborted).
 */
function afterFailure<T>(
  error: unknown,
  indices: number[],
  chunk: File[],
  busy: number,
  onRequest: (outcome: RequestOutcome<T>) => void,
  opts: Opts,
): number | "wait" | null {
  if (opts.signal?.aborted) return null;
  const max = opts.split ? tooManyMax(error) : null;
  if (max !== null && max < chunk.length) return max;
  if (opts.split && error instanceof ApiError && error.status === 429 && busy < MAX_BUSY_WAITS) return "wait";
  onRequest({ start: indices[0], indices, files: chunk, error });
  return null;
}
